import { Set } from 'immutable'
import { AsyncState, Fun, Updater, unloadedAsyncState } from 'widgets-for-react'
import {
  CollectionEditorState,
  collectionEditorStateUpdaters,
  initialCollectionEditorState,
} from '../../collections/collectionEditor/collectionEditorState'
import { loadDeleteComponent, loadSaveComponent } from './componentSaverApi'

export type ComponentSaverState = {
  selectedCollections: Set<string>
  deSelectedCollections: Set<string>
  collectionCreatorState: CollectionEditorState
  savingState: AsyncState<{}>
  deletingState: AsyncState<{}>
} & (
  | { opened: false }
  | {
      opened: true
      componentId: number
    }
)

export const initialComponentSaverState: ComponentSaverState = {
  opened: false,
  selectedCollections: Set(),
  deSelectedCollections: Set(),
  collectionCreatorState: initialCollectionEditorState,
  savingState: unloadedAsyncState(),
  deletingState: unloadedAsyncState(),
}

export const componentSaverStateUpdaters = {
  startSavingUpdater:
    (componentId: number) =>
    (state: ComponentSaverState): ComponentSaverState => ({
      ...state,
      opened: true,
      componentId: componentId,
      selectedCollections: Set(),
      deSelectedCollections: Set(),
      savingState: unloadedAsyncState(),
      deletingState: unloadedAsyncState(),
    }),
  stopSavingUpdater: (state: ComponentSaverState): ComponentSaverState => ({
    ...state,
    opened: false,
  }),
  selectedCollectionsUpdater:
    (collectionId: string) =>
    (state: ComponentSaverState): ComponentSaverState => {
      const collections = state.selectedCollections
      return {
        ...state,
        selectedCollections: collections.includes(collectionId)
          ? collections.remove(collectionId)
          : collections.add(collectionId),
      }
    },
  deSelectedCollectionsUpdater:
    (collectionId: string) =>
    (state: ComponentSaverState): ComponentSaverState => {
      const collections = state.deSelectedCollections
      return {
        ...state,
        deSelectedCollections: collections.includes(collectionId)
          ? collections.remove(collectionId)
          : collections.add(collectionId),
      }
    },
  savingStateUpdater:
    (updater: Updater<AsyncState<{}>>) =>
    (
      state: ComponentSaverState
    ): ComponentSaverState & { reloadCollections: boolean } => {
      const newState = updater(state.savingState)
      return {
        ...state,
        savingState: newState,
        reloadCollections:
          state.deletingState.kind == 'loaded' &&
          state.savingState.kind != 'loaded' &&
          newState.kind == 'loaded',
      }
    },
  deletingStateUpdater:
    (updater: Updater<AsyncState<{}>>) =>
    (
      state: ComponentSaverState
    ): ComponentSaverState & { reloadCollections: boolean } => {
      const newState = updater(state.deletingState)
      return {
        ...state,
        deletingState: newState,
        reloadCollections:
          state.savingState.kind == 'loaded' &&
          state.deletingState.kind != 'loaded' &&
          newState.kind == 'loaded',
      }
    },
  startLoadingSavingDeletingStateUpdater: (
    state: ComponentSaverState
  ): ComponentSaverState =>
    state.opened
      ? {
          ...state,
          savingState: loadSaveComponent(
            state.componentId,
            state.selectedCollections.toArray()
          ),
          deletingState: loadDeleteComponent(
            state.componentId,
            state.deSelectedCollections.toArray()
          ),
        }
      : state,
  collectionCreatorStateUpdater:
    (
      updater: Fun<
        CollectionEditorState,
        CollectionEditorState & {
          hideOuterModal: boolean
          reloadCollections?: boolean
        }
      >
    ) =>
    (
      state: ComponentSaverState
    ): ComponentSaverState & { reloadCollections?: boolean } => {
      const { hideOuterModal, reloadCollections, ...newState } = updater(
        state.collectionCreatorState
      )
      const selectedCollections =
        newState.editingState.kind == 'loaded' &&
        typeof newState.editingState.value === 'string'
          ? state.selectedCollections.add(newState.editingState.value)
          : state.selectedCollections
      return {
        ...(hideOuterModal
          ? componentSaverStateUpdaters.stopSavingUpdater(state)
          : state),
        collectionCreatorState: newState,
        selectedCollections: selectedCollections,
        reloadCollections: reloadCollections,
      }
    },
  startCreateCollectionUpdater: (
    state: ComponentSaverState
  ): ComponentSaverState => ({
    ...state,
    collectionCreatorState: collectionEditorStateUpdaters.startEditingUpdater(
      '',
      ''
    )(state.collectionCreatorState),
  }),
}
